import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { BrowserRouter } from 'react-router-dom';

import * as AppPropTypes from '../lib/PropTypes';

import { ApiProvider } from '../contexts/ApiContext';
import { AuthProvider } from '../contexts/AuthContext';
import { EditionProvider } from '../contexts/EditionContext';
import { FavoritesProvider } from '../contexts/FavoritesContext';
import { ItemRefProvider } from '../contexts/ItemRefContext';
import { KeysProvider } from '../contexts/KeysContext';
import { ListsProvider } from '../contexts/ListsContext';
import { ModalsProvider } from '../contexts/ModalsContext';
import { OnlineFilmsProvider } from '../contexts/OnlineFilmsContext';
import { PagesProvider } from '../contexts/PagesContext';
import { PurchasesProvider } from '../contexts/PurchasesContext';
import { RoutesProvider } from '../contexts/RoutesContext';
import { SearchProvider } from '../contexts/SearchContext';
import { SiteProvider } from '../contexts/SiteContext';
import App from './App';

const propTypes = {
    locale: PropTypes.string.isRequired,
    locales: PropTypes.arrayOf(PropTypes.string),
    keys: PropTypes.objectOf(PropTypes.string),
    translations: AppPropTypes.translations.isRequired,
    routes: AppPropTypes.routes.isRequired,
    handles: AppPropTypes.routes.isRequired,
    indexSlugs: AppPropTypes.routes.isRequired,
    pages: AppPropTypes.pages,
    menus: PropTypes.objectOf(AppPropTypes.menus),
    // user: AppPropTypes.user,
    // favorites: AppPropTypes.favorites,
    // purchases: AppPropTypes.purchases,
    // onlineFilms: AppPropTypes.onlineFilms,
    edition: AppPropTypes.edition,
    statusCode: PropTypes.number,
    timeZone: PropTypes.string,
};

const defaultProps = {
    locales: null,
    keys: null,
    pages: [],
    menus: [],
    // user: null,
    // favorites: null,
    // purchases: null,
    // onlineFilms: null,
    edition: null,
    statusCode: null,
    timeZone: 'America/Toronto',
};

const Container = ({
    locale,
    translations,
    locales,
    routes,
    handles,
    indexSlugs,
    pages: initialPages,
    menus,
    // user,
    // favorites,
    // purchases,
    // onlineFilms,
    keys,
    edition,
    statusCode,
    timeZone,
}) => {
    const { search } = typeof window !== 'undefined' ? window.location : {};
    const [firstPage = null] = initialPages || [];
    const { handle = null } = firstPage || {};
    const isHome = handle === 'home';

    const pages = useMemo(
        () =>
            (initialPages || []).reduce((obj, page) => {
                const { type = null, parent = null } = page;
                const { type: parentType = null } = parent || {};
                const finalType = parentType || type;
                return {
                    ...obj,
                    [finalType === 'index' ? `${page.url}${search}` : page.url]: page,
                };
            }, {}),
        [initialPages, search],
    );

    const lists = useMemo(
        () =>
            (initialPages || []).reduce((obj, page) => {
                const { items = null, pages: pagePages = null } = page;
                return {
                    ...obj,
                    [page.url]: items || pagePages || null,
                };
            }, {}),
        [initialPages, search],
    );

    const queryClient = useMemo(() => {
        const client = new QueryClient({
            defaultOptions: {
                queries: {
                    staleTime: Infinity,
                },
            },
        });
        if (firstPage !== null) {
            const query =
                window && !isEmpty(window.location.search)
                    ? queryString.parse(window.location.search, {
                          arrayFormat: 'bracket',
                      })
                    : null;
            client.setQueryData(['pages', firstPage.url, query], firstPage);
        }
        return client;
    }, [firstPage]);

    return (
        <IntlProvider
            locale={locale}
            timeZone={timeZone}
            messages={translations[locale] || translations}
        >
            <BrowserRouter>
                <RoutesProvider routes={routes} handles={handles} indexes={indexSlugs}>
                    <QueryClientProvider client={queryClient}>
                        <SiteProvider locales={locales} menus={menus} menuHidden={isHome}>
                            <KeysProvider keys={keys}>
                                <EditionProvider edition={edition}>
                                    <ApiProvider indexSlugs={indexSlugs}>
                                        <ModalsProvider>
                                            <AuthProvider>
                                                <OnlineFilmsProvider>
                                                    <FavoritesProvider>
                                                        <PurchasesProvider>
                                                            <PagesProvider pages={pages}>
                                                                <ListsProvider lists={lists}>
                                                                    <SearchProvider>
                                                                        <ItemRefProvider>
                                                                            <App
                                                                                locale={locale}
                                                                                indexSlugs={
                                                                                    indexSlugs
                                                                                }
                                                                                statusCode={
                                                                                    statusCode
                                                                                }
                                                                            />
                                                                        </ItemRefProvider>
                                                                    </SearchProvider>
                                                                </ListsProvider>
                                                            </PagesProvider>
                                                        </PurchasesProvider>
                                                    </FavoritesProvider>
                                                </OnlineFilmsProvider>
                                            </AuthProvider>
                                        </ModalsProvider>
                                    </ApiProvider>
                                </EditionProvider>
                            </KeysProvider>
                        </SiteProvider>
                    </QueryClientProvider>
                </RoutesProvider>
            </BrowserRouter>
        </IntlProvider>
    );
};

Container.propTypes = propTypes;
Container.defaultProps = defaultProps;

export default Container;
